/**
 * Image preloader for Eyes Follow Mouse Widget
 */

export class ImageLoader {
  private images: HTMLImageElement[] = [];
  private loadedCount = 0;
  private totalCount = 0;

  /**
   * Preload all images
   * @param imageUrls Array of image URLs to preload
   * @param onProgress Optional progress callback
   * @returns Promise that resolves with loaded images
   */
  public async preloadImages(
    imageUrls: string[],
    onProgress?: (loaded: number, total: number) => void
  ): Promise<HTMLImageElement[]> {
    this.totalCount = imageUrls.length;
    this.loadedCount = 0;
    this.images = [];

    const loadPromises = imageUrls.map((url, index) => {
      return this.loadSingleImage(url, index, onProgress);
    });

    this.images = await Promise.all(loadPromises);
    return this.images;
  }

  /**
   * Load a single image
   */
  private loadSingleImage(
    url: string,
    index: number,
    onProgress?: (loaded: number, total: number) => void
  ): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        this.loadedCount++;
        if (onProgress) {
          onProgress(this.loadedCount, this.totalCount);
        }
        resolve(img);
      };

      img.onerror = () => {
        console.error(`Failed to load image: ${url}`);
        // Still resolve with a placeholder to not break the array
        this.loadedCount++;
        if (onProgress) {
          onProgress(this.loadedCount, this.totalCount);
        }
        resolve(img); // Resolve anyway to not block other images
      };

      img.src = url;
      img.alt = `Gaze direction ${index}`;
    });
  }

  /**
   * Get loaded images
   */
  public getImages(): HTMLImageElement[] {
    return this.images;
  }

  /**
   * Get specific image by index
   */
  public getImage(index: number): HTMLImageElement | undefined {
    return this.images[index];
  }

  /**
   * Clear loaded images
   */
  public clear(): void {
    this.images = [];
    this.loadedCount = 0;
    this.totalCount = 0;
  }
}
